import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Grid2, LinearProgress, Stack, useTheme } from '@mui/material';
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import _ from 'lodash';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMenus } from '../common/dialog.util';
import { getRangeLabel, lighter } from '../common/util';
import { DialogsContext } from '../contexts';
import { termApi } from './term.queries';
import TermStudentCard from './term.student.card';

export default function TermCard(props) {
	const navigate = useNavigate();
	const useGet = termApi.useGet(props.id);
	const term = useGet?.data;

	const theme = useTheme();
	const menus = useMenus();
	const { dialogs } = useContext(DialogsContext);

	if (!term?.name) {
		return;
	}

	const href = "/terms/" + term.id;
	const linkActive = window.location.pathname != href;

	const students = _.chain(term.enrollments).orderBy('student.name').groupBy('studentId').map((enrollments, studentId) => {
		const student = enrollments[0].student;
		student.enrollments = enrollments.map(enrollment => _.omit(enrollment, 'student'));
		return student;
	}).value();

	const now = new Date().getTime();
	let progress = now < term.startDate ? 0 : now > term.endDate ? 100 : 100 * (now - term.startDate) / (term.endDate - term.startDate);
	let completeness = 0;
	if (term?.enrollments?.length) {
		completeness = _.sumBy(term?.enrollments, 'completeness') / term.enrollments.length;
	}

	const progressSx = {
		flexGrow: 1,
		height: 12,
		'.MuiLinearProgress-bar1Buffer': { backgroundColor: lighter(term.colour, 0.2, theme) },
		'.MuiLinearProgress-bar2Buffer': { backgroundColor: lighter(term.colour, 0.6, theme) },
		'.MuiLinearProgress-dashed': { opacity: '0 !important', borderTop: '1px solid black' },
	};

	const title = progress.toFixed(0) + '% Progress; ' + completeness.toFixed(0) + '% Complete';
	let progressBar;
	if (progress == completeness) {
		progressBar = <LinearProgress variant="buffer" value={completeness} valueBuffer={progress} sx={progressSx} color="info" title={title} />
	}
	else if (progress < completeness) {
		progressBar = <LinearProgress variant="buffer" value={progress} valueBuffer={completeness} sx={progressSx} color="success" title={title} />
	}
	else {
		progressBar = <LinearProgress variant="buffer" value={completeness} valueBuffer={progress} sx={progressSx} color="warning" title={title} />
	}

	const rangeLabel = getRangeLabel(term.startDate, term.endDate);

	const optionsMenu = (
		<Box>
			<IconButton className="hover-muted" onClick={(e) => menus.open('term', e)} sx={{ mr: -3 }}>
				<MoreVertIcon />
			</IconButton>
			<Menu open={menus.isOpen.term == true} onClose={() => menus.close()} anchorEl={menus.anchors.term}>
				<MenuItem onClick={() => { menus.close(); dialogs.open('term', term) }}>Edit</MenuItem>
			</Menu>
		</Box>
	);

	const cardContent = (
		<CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
			<Stack spacing={1}>
				<Stack sx={{ flex: 1 }} justifyContent="space-between">
					<Stack alignItems="baseline">
						<Typography variant="h5">{term.name}</Typography>
						<Typography variant="subtitle1">({term.enrollments?.length} Courses)</Typography>
					</Stack>
					<Typography variant="h5">{rangeLabel}</Typography>
				</Stack>
				{!linkActive && !props.hideMenu && optionsMenu}
			</Stack>

			<Grid2 container sx={{ width: '100%', py: 4 }} spacing={4}>
				{students.map((student) => (
					<Grid2 size={{ xs: 12, md: 6 }} key={student.id}>
						<TermStudentCard student={student} />
					</Grid2>
				))}
			</Grid2>

			<Box sx={{ flexGrow: 1 }}></Box>
			{completeness > 0 &&
				<Box sx={{ ml: -6, mr: -5 }}>
					<Typography variant="subtitle2" sx={{ ml: (completeness - 3) + '%' }}>{completeness.toFixed(0)}%</Typography>
					{progressBar}
				</Box>
			}
		</CardContent>
	);

	let cardDetail = (
		<Grid2 container sx={{ width: '100%' }}>
			<Grid2 size={12} sx={{ display: 'flex' }}>
				{cardContent}
			</Grid2>
		</Grid2>
	);

	if (linkActive) {
		cardDetail = (
			<CardActionArea onClick={() => navigate(href)} sx={{ display: 'flex', textDecoration: 'none', height: '100%' }}>
				{cardDetail}
			</CardActionArea>
		);
	}

	const cardSx = { width: '100%', background: lighter(term.colour, 0.8, theme) };
	return (
		<Card sx={cardSx} className="hover-toggle">
			{cardDetail}
		</Card>
	);
}